import { Fragment, PropsWithChildren, useEffect, useRef } from 'react'

import Cookies from 'js-cookie'
import { Helmet } from 'react-helmet'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { useIntl } from '@context/Internationalization/useIntl'
import { IApplicationState } from '@store'
import { selectAuthToken } from '@store/auth'
import { fetchMeRequest, selectMeIsFetched } from '@store/me'
import { fetchRequest as fetchRootResource, selectRootResourceIsFetched } from '@store/rootResource'
import { showCookieToast } from '@utils'

export const Main: React.FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch()
  const { intl } = useIntl()
  const { isLoggedIn, isMeFetched, isRootResourceFetched } = useSelector(
    (state: IApplicationState) => ({
      isLoggedIn: Boolean(selectAuthToken(state)),
      isMeFetched: selectMeIsFetched(state),
      isRootResourceFetched: selectRootResourceIsFetched(state),
    }),
    shallowEqual,
  )

  const calledRef = useRef(false)

  const fetchMeRequestAction = () => dispatch(fetchMeRequest())
  const fetchRootResourceAction = () => dispatch(fetchRootResource())

  useEffect(() => {
    if (!calledRef.current) {
      fetchRootResourceAction()
      if (isLoggedIn) {
        fetchMeRequestAction()
      }
      calledRef.current = true
    }
  }, [isLoggedIn])

  // Handle cookie toast on mount and updates
  useEffect(() => {
    if (!Cookies.get('tribecookieread') && isLoggedIn) {
      showCookieToast()
    }
  }, [isLoggedIn])

  const isRootAndMeIsFetched = isRootResourceFetched && isMeFetched
  const isReady = isLoggedIn ? isRootAndMeIsFetched : isRootResourceFetched

  return (
    isReady && (
      <Fragment>
        <Helmet
          titleTemplate={`${intl?.formatMessage({
            id: 'core.text.tribe',
          })} - %s`}
        />

        <main className="main">{children}</main>
      </Fragment>
    )
  )
}

export default Main
